import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { fetchAdminTransactions, processRefund } from '../../api/admin';
import { AdminDataTable, AdminSearchBar, AdminPagination, AdminConfirmModal, Column } from '../../components/admin';

const filterOptions = [
  { label: 'All', value: '' },
  { label: 'Deposits', value: 'DEPOSIT' },
  { label: 'Withdrawals', value: 'WITHDRAW' },
];

const statusOptions = [
  { label: 'All Status', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Success', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
  { label: 'Refunded', value: 'REFUNDED' },
];

const AdminPayments: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [refundTarget, setRefundTarget] = useState<any>(null);
  const pageSize = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    fetchAdminTransactions(params)
      .then((res) => { setTransactions(res.data.results); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRefund = async () => {
    if (!refundTarget) return;
    try {
      await processRefund(refundTarget.id, 'Admin refund');
      setRefundTarget(null);
      load();
    } catch {}
  };

  const columns: Column[] = [
    { key: 'id', header: 'ID', width: '60px' },
    { key: 'user_id', header: 'User', width: '70px' },
    {
      key: 'type', header: 'Type', width: '90px',
      render: (row) => (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
          backgroundColor: row.type === 'DEPOSIT' ? '#10B98120' : '#EF444420',
          color: row.type === 'DEPOSIT' ? '#10B981' : '#EF4444',
        }}>{row.type}</span>
      ),
    },
    { key: 'amount', header: 'Amount', render: (row) => `Rs ${row.amount}` },
    {
      key: 'status', header: 'Status', width: '100px',
      render: (row) => {
        const colorMap: Record<string, string> = { SUCCESS: '#10B981', PENDING: '#F59E0B', FAILED: '#EF4444', REFUNDED: '#8B5CF6' };
        const c = colorMap[row.status] || colors.grayScale500;
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${c}20`, color: c }}>{row.status}</span>;
      },
    },
    { key: 'description', header: 'Description', render: (row) => <span className="truncate max-w-[200px] block">{row.description || '-'}</span> },
    { key: 'created_at', header: 'Date', render: (row) => row.created_at?.split('T')[0] },
    {
      key: 'actions', header: '', width: '80px',
      render: (row) => row.status === 'SUCCESS' ? (
        <button
          onClick={(e) => { e.stopPropagation(); setRefundTarget(row); }}
          className="px-2 py-1 rounded text-xs font-medium"
          style={{ backgroundColor: '#EF444420', color: '#EF4444' }}
        >
          Refund
        </button>
      ) : null,
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>Payments & Transactions</h1>

      <div className="flex flex-wrap gap-3 mb-4">
        <AdminSearchBar
          value=""
          onChange={() => {}}
          placeholder=""
          filterOptions={filterOptions}
          filterValue={typeFilter}
          onFilterChange={(v) => { setTypeFilter(v); setPage(1); }}
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-xl text-sm outline-none"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
        >
          {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <AdminDataTable columns={columns} data={transactions} loading={loading} emptyMessage="No transactions found" />
      <AdminPagination page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage} />

      {refundTarget && (
        <AdminConfirmModal
          title="Process Refund"
          message={`Refund Rs ${refundTarget.amount} for transaction #${refundTarget.id}?`}
          confirmLabel="Refund"
          destructive
          onConfirm={handleRefund}
          onCancel={() => setRefundTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminPayments;
